import { useEffect } from 'react';

import useLocalStorage from '~/hooks/useLocalStorage';

import {
  DEFAULT_SUGGESTION_QUERY_CONFIG,
  type SuggestionQueryConfig,
  mergeDefaultSuggestionQueryConfig,
  sanitiseSuggestionQueryConfig,
} from './categoriseSearchGroups';

export const SUGGESTION_QUERY_CONFIG_STORAGE_KEY = 'analytics.suggestionQueryConfig';

export function useSuggestionQueryConfig() {
  const [storedValue, setStoredValue, clearValue] = useLocalStorage(SUGGESTION_QUERY_CONFIG_STORAGE_KEY, null) as [
    SuggestionQueryConfig | null,
    (value: SuggestionQueryConfig) => void,
    () => void,
  ];

  const config = storedValue === null ? DEFAULT_SUGGESTION_QUERY_CONFIG : sanitiseSuggestionQueryConfig(storedValue);
  const isCustomised = storedValue !== null;

  // When the admin opens the page, ensure the stored config still includes every default
  // prefix and suffix. If any are missing, merge them back in and persist so localStorage
  // always carries the defaults. Runs only on mount so in-session edits in the config dialog
  // are preserved until the page is reopened.
  useEffect(() => {
    if (storedValue === null) {
      return;
    }

    const sanitised = sanitiseSuggestionQueryConfig(storedValue);
    const merged = mergeDefaultSuggestionQueryConfig(sanitised);

    if (
      JSON.stringify(sanitised.prefixes) !== JSON.stringify(merged.prefixes) ||
      JSON.stringify(sanitised.suffixes) !== JSON.stringify(merged.suffixes)
    ) {
      setStoredValue(merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot migration on open
  }, []);

  const setConfig = (next: SuggestionQueryConfig) => {
    setStoredValue(sanitiseSuggestionQueryConfig(next));
  };

  return { config, setConfig, resetConfig: clearValue, isCustomised };
}
